import { useCallback, useEffect, useRef, useState } from "react";
import { IB_ACTIVE_ACCOUNT_CHANGED_EVENT } from "../Constants/accountTypes";
import {
  filterStableMarketQuotes,
  getStablesApiErrorMessage,
  getStablesBalances,
  mergeAssetsWithQuoteRates,
  supplementMissingMarketQuotes,
} from "../Services/stablesApi";
import {
  getCachedMarketQuotes,
  invalidateMarketQuotesCache,
} from "../Services/marketQuotesCache";
import {
  DISPLAY_MARKET_QUOTE_AMOUNT,
  STABLES_WALLET_CREATED_EVENT,
} from "../Pages/Private/Stables/constants";

const EMPTY_BALANCES = {
  brlBalance: 0,
  totalStablesBrl: 0,
  assets: [],
};

const computeTotalStablesBrl = (assets) =>
  assets.reduce((sum, asset) => {
    const qty = Number(asset.balance) || 0;
    const rate = Number(asset.rate) || 0;
    if (qty <= 0 || rate <= 0) return sum;
    // brl_equivalent = StableBalance qty × unit rate (4dp). Never treat qty as BRL.
    return sum + qty * rate;
  }, 0);

/**
 * @param {object} [options]
 * @param {boolean} [options.enabled]
 * @param {boolean} [options.ownedOnly] Reserved for callers (e.g. Swap). Balances API
 *   already returns owned wallet rows only — zero-balance keys are kept so create refresh works.
 */
export const useStablesBalances = ({ enabled = true, ownedOnly = false } = {}) => {
  const [data, setData] = useState(EMPTY_BALANCES);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState(null);
  const loadGenerationRef = useRef(0);

  const load = useCallback(async ({ force = false } = {}) => {
    if (!enabled) {
      setLoading(false);
      setError(null);
      return;
    }

    const generation = ++loadGenerationRef.current;
    setLoading(true);
    setError(null);

    try {
      const [balancesResult, quotesResult] = await Promise.all([
        getStablesBalances(),
        // Same cache key as StablesQuotesCarousel — do not invent a separate rate path.
        getCachedMarketQuotes(
          { side: "buy", mode: "amount", amount: DISPLAY_MARKET_QUOTE_AMOUNT },
          { force }
        ),
      ]);

      if (generation !== loadGenerationRef.current) return;

      const balanceAssets = balancesResult.assets ?? [];
      const balanceAssetIds = balanceAssets.map((asset) => asset.id);
      const quotedItems = await supplementMissingMarketQuotes(
        quotesResult.items ?? [],
        balanceAssetIds
      );
      const stableQuotes = filterStableMarketQuotes(quotedItems);

      const assets = mergeAssetsWithQuoteRates(
        balanceAssets,
        stableQuotes
      );

      // Keep all wallet rows (incl. zero-balance after create).
      const finalAssets = assets;

      const pricedAssets = finalAssets.filter((asset) => asset.rate > 0);

      setData({
        ...balancesResult,
        assets: finalAssets,
        totalStablesBrl: computeTotalStablesBrl(pricedAssets),
      });
    } catch (err) {
      if (generation !== loadGenerationRef.current) return;
      setData(EMPTY_BALANCES);
      setError(getStablesApiErrorMessage(err, "Não foi possível carregar os saldos."));
    } finally {
      if (generation === loadGenerationRef.current) {
        setLoading(false);
      }
    }
  }, [enabled, ownedOnly]);

  const reload = useCallback(() => {
    if (!enabled) return Promise.resolve();
    invalidateMarketQuotesCache();
    return load({ force: true });
  }, [enabled, load]);

  // TODO: remove when Baas /me provisioning ready - test mode only
  // Restore token gate: skip fetch when !isAutenticated || !token?.trim()
  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      setError(null);
      return;
    }
    load();
  }, [enabled, load]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onAccountChanged = () => {
      void reload();
    };
    window.addEventListener(IB_ACTIVE_ACCOUNT_CHANGED_EVENT, onAccountChanged);
    return () => window.removeEventListener(IB_ACTIVE_ACCOUNT_CHANGED_EVENT, onAccountChanged);
  }, [enabled, reload]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onWalletCreated = () => {
      void reload();
    };
    window.addEventListener(STABLES_WALLET_CREATED_EVENT, onWalletCreated);
    return () => window.removeEventListener(STABLES_WALLET_CREATED_EVENT, onWalletCreated);
  }, [enabled, reload]);

  return {
    ...data,
    loading,
    error,
    reload,
  };
};
